// src/components/CartCrossSell.tsx
//
// Compact "You might also like" row inside the cart drawer: up to two catalog
// siblings of what's already in the cart (same categories, not already added).
// Clicking navigates to the product page — variation (color/size) selection
// still happens there, so nothing is blind-added to the cart.

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CatalogObject } from "square";
import { useCart } from "../contexts/CartContext";
import { useCatalog } from "../contexts/CatalogContext";
import { findOrGetImages } from "../api";
import { getTabLink } from "../extensions/helpers";

const MAX_SUGGESTIONS = 2;

const CartCrossSell: React.FC = () => {
  const navigate = useNavigate();
  const { cart, setIsCartOpen } = useCart();
  const {
    getItemsByCategory,
    getLowestCategoryByItem,
    getCategoryById,
    getImagesByItem,
    getImagesByIds,
    addImages,
  } = useCatalog();

  const suggestions = useMemo(() => {
    const cartIds = new Set(cart.map((entry) => entry.product.id));
    const picked: CatalogObject[] = [];
    for (const entry of cart) {
      const category = getLowestCategoryByItem(entry.product.id ?? "");
      for (const item of getItemsByCategory(category?.id ?? "") ?? []) {
        if (!cartIds.has(item.id) && !picked.some((p) => p.id === item.id)) {
          picked.push(item);
          if (picked.length >= MAX_SUGGESTIONS) return picked;
        }
      }
    }
    return picked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  // Fetch the suggestions' images, same as every other list that renders
  // products (FeaturedProductList, SearchMenu, ProductLinePage, WishlistPage).
  // CatalogProvider never loads IMAGE objects itself — the cache fills lazily as
  // a visitor opens product pages — and a cross-sell suggestion is BY
  // DEFINITION a product they haven't opened, so its thumbnail was always
  // broken. Most visible to someone arriving straight from a link (ad, Meta
  // shop, shared URL) who has browsed nothing at all.
  useEffect(() => {
    if (suggestions.length === 0) return;
    // Fire-and-forget: a missing thumbnail must never surface as an error.
    findOrGetImages(suggestions, addImages, getImagesByIds).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions]);

  if (cart.length === 0 || suggestions.length === 0) return null;

  const openProduct = (item: CatalogObject) => {
    const category = getLowestCategoryByItem(item.id ?? "");
    const domain = getCategoryById(
      category?.categoryData?.parentCategory?.id ?? "",
    );
    setIsCartOpen(false);
    navigate(
      getTabLink({
        domain: domain?.categoryData?.name ?? undefined,
        category: category?.categoryData?.name ?? undefined,
        itemName: item.itemData?.name ?? undefined,
      }),
      { state: { selectedProduct: item } },
    );
  };

  return (
    <div className="border-t border-gray-200 mt-4 px-5 pb-4 pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
        You might also like
      </h3>
      <ul className="flex flex-col gap-2">
        {suggestions.map((item) => {
          const imageUrl = getImagesByItem(item.id ?? "")?.[0]?.imageData?.url;
          const name = item.itemData?.name ?? "";
          // Square amounts are integer cents; a variation without a price would
          // otherwise render "$NaN".
          const cents = Number(
            item.itemData?.variations?.[0]?.itemVariationData?.priceMoney
              ?.amount,
          );
          const price = Number.isFinite(cents)
            ? `$${(cents / 100).toFixed(2)}`
            : null;

          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => openProduct(item)}
                className="group flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white p-2 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                {/* A plain grey tile beats a broken-image icon when the catalog
                    image cache hasn't filled yet or an image 404s. */}
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="h-14 w-14 flex-shrink-0 rounded-md bg-gray-100 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="h-14 w-14 flex-shrink-0 rounded-md bg-gray-100"
                    aria-hidden="true"
                  />
                )}

                <div className="min-w-0 flex-1">
                  {/* min-w-0 + truncate: without it a long product name forces
                      the row wider than the cart drawer. */}
                  <p className="truncate text-sm font-medium text-gray-900">
                    {name}
                  </p>
                  {price && (
                    <p className="mt-0.5 text-sm text-gray-600">{price}</p>
                  )}
                </div>

                <svg
                  className="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CartCrossSell;
